/**
 * Helpers to separate LinkedIn people from company/institutional pages.
 */

/**
 * @param {object} res
 * @returns {boolean}
 */
export function isLinkedInPeopleSearchRow(res) {
  if (!res) return false;
  if (String(res.relation_type || '') === 'LinkedIn People Search') return true;
  if (String(res.details?.search_kind || '') === 'linkedin_people_directory') return true;
  const url = String(res.url || '').toLowerCase();
  return url.includes('linkedin.com/search/results/people');
}

/**
 * @param {object} res
 * @returns {boolean}
 */
export function isLinkedInPersonProfile(res) {
  if (isLinkedInPeopleSearchRow(res)) return false;
  const url = String(res?.url || '').toLowerCase();
  return /linkedin\.com\/in\//.test(url);
}

/**
 * Company pages, school pages, and non-/in/ institutional URLs
 * (e.g. life.ro brand pages, LinkedIn company profiles).
 *
 * @param {object} res
 * @returns {boolean}
 */
export function isAssociatedEntityRow(res) {
  if (!res || isLinkedInPeopleSearchRow(res)) return false;
  const url = String(res.url || '').toLowerCase();
  if (!url) return false;
  if (/linkedin\.com\/(company|school|showcase)\//.test(url)) return true;
  if (isLinkedInPersonProfile(res)) return false;
  if (res.ui_bucket === 'relationship_node' && !/linkedin\.com\/in\//.test(url)) {
    return true;
  }
  return false;
}

/**
 * Build LinkedIn People Search URL from employer + location context.
 *
 * @param {{
 *   companyName?: string,
 *   domain?: string,
 *   locations?: string[],
 * }} opts
 * @returns {{ url: string, keywords: string }}
 */
export function buildLinkedInPeopleSearchUrl({
  companyName = '',
  domain = '',
  locations = [],
} = {}) {
  const parts = [];
  const name = String(companyName || '').trim();
  const host = String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (name) parts.push(name);
  else if (host) parts.push(host.split('.')[0]);
  for (const loc of locations || []) {
    const t = String(loc || '').trim();
    if (!t) continue;
    parts.push(t);
    break;
  }
  const keywords = parts.join(' ').trim();
  if (!keywords) return { url: '', keywords: '' };
  const params = new URLSearchParams({
    keywords,
    origin: 'SWITCH_SEARCH_VERTICAL',
  });
  return {
    url: `https://www.linkedin.com/search/results/people/?${params.toString()}`,
    keywords,
  };
}

/**
 * @param {string} domain
 * @returns {boolean}
 */
export function isInternationalApexDomain(domain) {
  const host = String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
  if (!host || !host.includes('.')) return false;
  const tld = host.split('.').pop();
  return tld === 'com' || tld === 'eu';
}

/**
 * Initials avatar from a display name.
 *
 * @param {string} name
 * @returns {string}
 */
export function personInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

/**
 * Headline / role from title or snippet (single line).
 *
 * @param {object} res
 * @param {string} personName
 * @returns {string}
 */
export function personRoleLine(res, personName) {
  const title = String(res?.title || '').trim();
  const snip = String(res?.snippet || res?.relation_type || '').trim();
  const name = String(personName || '').trim();
  let role = title;
  if (name && title.toLowerCase().startsWith(name.toLowerCase())) {
    role = title.slice(name.length).replace(/^[\s\-–—|:]+/, '');
  }
  if (!role || role.length < 2) role = snip;
  if (!role) role = res?.relation_type || 'Key person';
  return role.replace(/\s+/g, ' ').trim();
}
